import React, { useState, useEffect, useCallback } from 'react';
import { MdCached, MdUpdate, MdCloudOff } from 'react-icons/md';
import cacheStore, { MODE_SWR, subscribe } from './cacheStore';
import useNetworkStatus from './useNetworkStatus';
import './cacheToggle.css';

/**
 * CacheToggle — the navbar switch between «التخزين المؤقت» and «مباشر فقط».
 *
 * Rebuilt from src/components/common/CacheToggle.js, keeping the shape
 * the user liked — pill container, small switch, MdCached/MdUpdate, the
 * Arabic label, a pulsing status dot — but with three changes:
 *
 *   1. It reads its own state from cacheStore instead of being a
 *      controlled component. The old one took `cacheEnabled` as a prop
 *      and called `cacheService.toggle()`, so the prop and the service
 *      could disagree; whoever rendered it had to mirror the state by
 *      hand. Subscribing to the store means the toggle, every mounted
 *      useCachedData and any other copy of this component can never
 *      drift apart.
 *   2. New tokens. The old CSS was hard-coded for a dark navbar
 *      (#a0aec0 text on rgba(255,255,255,0.03)); this shell's top bar is
 *      --ui-surface, i.e. white, where that was invisible.
 *   3. The status dot now also reports CONNECTIVITY, not just the
 *      setting. Offline is the moment a user most needs to know the
 *      cache is what is holding the screen together.
 *
 * Renders nothing but a button — no layout assumptions — so it can be
 * dropped straight into .shell-top.
 */
export default function CacheToggle({ className = '' }) {
  const [mode, setMode] = useState(() => cacheStore.getMode());
  const { offline } = useNetworkStatus();

  // useSyncExternalStore would also work; a subscription is clearer here
  // and this component re-renders perhaps twice a day.
  useEffect(() => subscribe(() => setMode(cacheStore.getMode())), []);

  const enabled = mode === MODE_SWR;

  const handleToggle = useCallback(() => {
    cacheStore.toggleMode();
  }, []);

  // Offline + live-only is the one genuinely broken combination: the
  // user has told us never to read the cache and there is no network to
  // read instead. Say so rather than showing a cheerful grey dot.
  const strandedOffline = offline && !enabled;

  const title = offline
    ? (enabled
      ? 'غير متصل — تُعرض البيانات المخزّنة'
      : 'غير متصل ووضع «مباشر فقط» مفعّل — لن تُحمَّل بيانات')
    : (enabled ? 'إيقاف التخزين المؤقت' : 'تفعيل التخزين المؤقت');

  let dotState = 'inactive';
  if (offline) dotState = strandedOffline ? 'danger' : 'warn';
  else if (enabled) dotState = 'active';

  return (
    <div className={`ui-cache-toggle${enabled ? ' is-on' : ''}${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        className={`ui-cache-switch${enabled ? ' is-on' : ''}`}
        onClick={handleToggle}
        role="switch"
        aria-checked={enabled}
        aria-label={enabled ? 'إيقاف التخزين المؤقت' : 'تفعيل التخزين المؤقت'}
        title={title}
      >
        <span className="ui-cache-knob" />
      </button>

      <span className="ui-cache-label" title={title}>
        <span className="ui-cache-icon" aria-hidden="true">
          {offline ? <MdCloudOff /> : (enabled ? <MdCached /> : <MdUpdate />)}
        </span>
        <span className="ui-cache-text">
          {enabled ? 'التخزين المؤقت' : 'مباشر فقط'}
        </span>
      </span>

      <span className={`ui-cache-dot is-${dotState}`} aria-hidden="true" />
    </div>
  );
}
